import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Wordmark } from "../components/Brand";
import { apiError } from "../lib/api";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8"><Wordmark size="text-2xl" />
          <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#78716C]">Operations Hub — Password reset</div></div>
        <div className="card p-8 space-y-5 fade-up">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 size={36} className="mx-auto text-[#4A7C59]" />
              <h1 className="font-serif font-bold text-xl text-navy">Password updated</h1>
              <p className="text-sm text-[#57534E]">Your password has been reset. All previous sessions were signed out — sign in with your new password.</p>
              <button className="btn-primary w-full" onClick={() => navigate("/login")} data-testid="reset-goto-login">Go to sign in</button>
            </div>
          ) : !token ? (
            <div className="space-y-4" data-testid="reset-missing-token">
              <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#991B1B]">
                This reset link is incomplete. Use the full link from your email.</div>
              <Link to="/forgot-password" className="btn-outline w-full" data-testid="reset-request-new">Request a new reset link</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <h1 className="font-serif font-bold text-xl text-navy">Choose a new password</h1>
                <p className="text-sm text-[#57534E] mt-1">Minimum 10 characters. This link works once and expires 1 hour after it was sent.</p>
              </div>
              <div><label className="label">New password</label>
                <div className="relative">
                  <input type={show ? "text" : "password"} className="input w-full !pr-11" value={password}
                    onChange={(e) => setPassword(e.target.value)} required minLength={10} data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShow(!show)} data-testid="reset-password-toggle"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-navy transition-colors"
                    title={show ? "Hide password" : "Show password"}>
                    {show ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div></div>
              <div><label className="label">Confirm password</label>
                <input type={show ? "text" : "password"} className="input" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required minLength={10} data-testid="reset-confirm-input" /></div>
              {error && <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#991B1B]" data-testid="reset-error">{error}</div>}
              <button className="btn-primary w-full" disabled={busy} data-testid="reset-submit-button">{busy ? "Updating…" : "Set new password"}</button>
              <Link to="/login" className="block text-center text-xs font-bold text-terracotta hover:underline" data-testid="reset-back-link">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
